import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api } from '@/lib/api.js';

const SOURCES = ['Facebook', 'Referral', 'Cold inquiry', 'Existing client', 'Other'];

export default function LeadCaptureSheet({ open, onOpenChange, onSuccess }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.post('/api/tracon/command-center/pattern/leads', {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        source: source || null,
        note: note.trim() || null,
      });

      toast.success(`Lead captured: ${name.trim()}`);
      onSuccess?.(result);

      if (result?.deal_id) {
        const accountId = result.contact_id || result.deal_id;
        navigate(`/account/${encodeURIComponent(accountId)}/deal/${encodeURIComponent(result.deal_id)}`);
      }
    } catch (err) {
      toast.error(`Failed: ${err.message || 'unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New lead</SheetTitle>
          <SheetDescription>Capture a new business inquiry</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-text-muted">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 555-1234"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">Source</label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text"
            >
              <option value="">Select source…</option>
              {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">First-touch note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="How did they find us? What do they need?"
              className="mt-1 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim"
            />
          </div>

          <Button onClick={handleSubmit} disabled={submitting} className="w-full">
            {submitting ? 'Saving…' : 'Capture lead'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
